/** @jsx jsx */
import { jsx } from '@emotion/react';
import type { MouseEvent, ReactNode } from 'react';
import type { AnchorTarget, IconName } from '@zapier/design-system';
import {
  Icon,
  Link,
  TagLabel,
  Tooltip,
  Typography,
} from '@zapier/design-system';
import { createUniqueClassName } from '../../utils/createUniqueClassName';
import { useUniversalNavContext } from '../../hooks';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

export type Props = {
  /**
   * The URL to where the link goes.
   */
  href: string | null;
  /**
   * The `Icon` component to render within the link.
   * **Note that if renderBeforeText is defined, that prop is then responsible for rendering.**
   */
  icon?: IconName;
  /**
   * Indicates whether the link is collapsed down to just an icon.
   */
  isCollapsed?: boolean;
  /**
   * Indicates whether the link goes to a page outside of
   * the logged in app experience.
   */
  isExternal?: boolean;
  /**
   * Indicates whether the link is the selected/current link.
   */
  isSelected?: boolean;
  /**
   * This value is true if this instance is in the UniversalSidebar
   */
  isSidebar?: boolean;
  /**
   * If true, adjusts layout for v3 sidebar links
   */
  isV3Link?: boolean;
  /**
   * Optional click handler for the link.
   */
  onClick?: React.MouseEventHandler;
  /**
   * Renders content before the link text. E.g; an Icon or Avatar
   */
  renderBeforeText?: (props: Props) => ReactNode;
  /**
   * The `role` attribute of the `UniversalNavLink`, which specifies
   * the function of the nested `Link`.
   */
  role?: string;
  /**
   * The secondary text content to render within the link.
   */
  secondaryText?: string;
  /**
   * Indicates whether to show a checkmark on the selected item
   */
  showCheckmarkOnSelected?: boolean;
  /**
   * The text content to render within the link.
   */
  text: string;
  /**
   * Optional target for the link.
   **/
  target?: AnchorTarget;
  /**
   * Optional tag for the link. Typically "alpha" or "beta".
   **/
  tag?: string;
};

const tooltipClassName = createUniqueClassName('UniversalNavLink', 'tooltip');
const tooltipSelector = `.${tooltipClassName}`;

const navIconClassName = createUniqueClassName('UniversalNavLink', 'navIcon');
const navIconSelector = `.${navIconClassName}`;

const externalIconClassName = createUniqueClassName(
  'UniversalNavLink',
  'externalIcon'
);
const externalIconSelector = `.${externalIconClassName}`;

const Styles = labelStyles('UniversalNavLink', {
  root: (
    props: Pick<Props, 'isSidebar' | 'isCollapsed' | 'isSelected'>,
    hasBeforeText: boolean
  ) => [
    // Rare specificity bump over `Link`.
    withModifier('', {
      font: `var(--zds-body-2, 500 14px/24px ${Typography.base.fontFamily})`,
      color: 'var(--zds-text-default, #413735)',
      display: 'flex',
      position: 'relative',
      minHeight: 'var(--zds-size-36, 36px)',
      padding: 'var(--zds-space-0, 0px) var(--zds-space-8, 8px)',
      borderRadius: 4,
      whiteSpace: 'nowrap',
      width: '100%',
      transition: `0s`,
      textDecoration: 'none',

      '&:hover, &:focus': {
        transition: 'none',
        backgroundColor: 'var(--zds-background-stronger, #F5F3EB)',
        outline: 'none',
      },
      [navIconSelector]: {
        color: 'var(--zds-text-default, #413735)',
      },
      [`&:hover ${navIconSelector}, &:focus ${navIconSelector}`]: {
        transition: 'none',
        color: 'var(--zds-text-default, #413735)',
      },
      ...(props.isSidebar && {
        borderRadius: '4px',
        color: 'var(--zds-text-default, #413735)',
        font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
        outline: 'solid 2px transparent',
        // This is the outline that renders around the button when
        // its focused. Its a pseudo element to preserve transparency;
        // between the edge of the button and the edge of the outline.
        // Its defined here vs only in ':focus' so that it can transition
        // between states.
        '&::before': {
          content: '""',
          position: 'absolute',
          pointerEvents: 'none',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          border: '2px solid transparent',
          borderRadius: 4,
        },
        '&:focus': {
          backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',
        },
        '&:hover': {
          transition: 'none',
          backgroundColor: 'var(--zds-background-stronger, #F5F3EB)',
          outline: `solid 2px transparent`,
        },
        '&:focus-visible': {
          '&::before': {
            border: 'var(--zds-border-focus, 2px solid #695BE8)',
            top: '-2px',
            right: '-2px',
            bottom: '-2px',
            left: '-2px',
          },
        },
        [navIconSelector]: {
          color: 'var(--zds-text-default, #413735)',
        },
        [`&:hover ${navIconSelector}, &:focus ${navIconSelector}`]: {
          transition: 'none',
        },
      }),
    }),
    !hasBeforeText &&
      withModifier('textOnly', {
        paddingLeft: 15,
        paddingRight: 15,
      }),
    props.isSelected &&
      withModifier('selected', {
        color: 'var(--zds-ui-primary-strongest, #503EBD)',
        backgroundColor: 'var(--zds-ui-primary-weakest, #F7F6FD)',
        [navIconSelector]: {
          color: 'var(--zds-ui-primary-strongest, #503EBD)',
        },
        ...(props.isSidebar && {
          font: `var(--zds-body-3, 600 14px/24px ${Typography.base.fontFamily})`,
          color: 'var(--zds-text-default, #413735)',
          backgroundColor: 'var(--zds-ui-brand-weakest, #FDF5F2)',
          [navIconSelector]: { color: 'var(--zds-ui-brand, #FF4F00)' },
        }),
        '&:hover, &:focus, &:focus-visible': {
          backgroundColor: props.isSidebar
            ? 'var(--zds-ui-brand-weakest, #FDF5F2)'
            : 'var(--zds-ui-primary-weakest, #F7F6FD)',
        },
      }),
    props.isCollapsed &&
      withModifier('collapsed', {
        width: 'var(--zds-space-36, 36px)',
      }),
    {
      [tooltipSelector]: {
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.1s ease-in-out',
        '@media (hover: none)': {
          display: 'none',
        },
      },
      [`&:hover ${tooltipSelector}, &:focus ${tooltipSelector}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    {
      [externalIconSelector]: {
        opacity: 0,
      },
      [`&:hover ${externalIconSelector}, &:focus ${externalIconSelector}`]: {
        opacity: 0.5,
      },
    },
  ],
  layout: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: 20,
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  v3Layout: {
    display: 'flex',
    gap: 'var(--zds-space-12, 12px)',
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  text: (showCheckmark: { showCheckmark: boolean }) => [
    {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      '&[aria-hidden="true"]': {
        opacity: 0,
      },
    },
    // if there is a checkmark set to show on the selected item, make the text container grow so the checkmark is right aligned
    showCheckmark &&
      withModifier('showCheckmark', {
        flexGrow: 1,
      }),
  ],
  primaryText: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },

  secondaryText: (isSelected: boolean) => [
    {
      font: `var(--zds-small-print-1, 400 14px/20px ${Typography.base.fontFamily})`,
      color: 'var(--zds-gray-warm-9, #413735)',
    },
    isSelected &&
      withModifier('selected', {
        color: 'var(--zds-ui-primary-strongest, #503EBD)',
      }),
  ],
});

/**
 * @private
 * Renders a link within the universal sidebar.
 */
const UniversalNavLink = ({
  href,
  text,
  icon = undefined,
  isCollapsed = false,
  isExternal = false,
  isSelected = false,
  isSidebar = undefined,
  isV3Link = false,
  onClick: onClickProp = undefined,
  role = undefined,
  secondaryText = undefined,
  showCheckmarkOnSelected = false,
  tag = undefined,
  target = undefined,
  renderBeforeText = (props: Props) =>
    props.icon && (
      <span className={navIconClassName}>
        <Icon isBlock={true} name={props.icon} size={isSidebar ? 20 : 18} />
      </span>
    ),
}: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const props = {
    href,
    text,
    icon,
    isCollapsed,
    isExternal,
    isSelected,
    isSidebar,
    onClick: onClickProp,
    role,
    target,
    renderBeforeText,
  } satisfies Props;

  const tooltip = isCollapsed && (
    <span className={tooltipClassName}>
      <Tooltip position="east">{text}</Tooltip>
    </span>
  );
  const onClick = (e: MouseEvent) => {
    // Chrome is the only browser that causes an element to
    // be in a `:focus` state when it's clicked. That `:focus`
    // state causes `Tooltip` to undesirably render. The user
    // already clicked, so they don't need to see the tooltip.
    // To hide the tooltip, `blur` the node to cause it to no
    // longer have `:focus`. Tabbing location is still preserved.
    (e.currentTarget as HTMLElement).blur();
    if (onClickProp) {
      onClickProp(e);
    }
  };
  const beforeText = renderBeforeText && renderBeforeText(props);

  return (
    <Link
      color={null}
      css={Styles.root({ isSidebar, isCollapsed, isSelected }, !!beforeText)}
      href={getFullyQualifiedURL(href)}
      onClick={onClick}
      role={role}
      target={target || '_self'}
    >
      <span css={isV3Link ? Styles.v3Layout : Styles.layout} data-zds>
        {beforeText}
        <span
          aria-hidden={isCollapsed}
          css={Styles.text(showCheckmarkOnSelected)}
          data-zds
        >
          <span aria-hidden={isCollapsed} css={Styles.primaryText} data-zds>
            {text}
          </span>
          {secondaryText && (
            <span
              aria-hidden={isCollapsed}
              css={Styles.secondaryText(isSelected)}
              data-zds
            >
              {secondaryText}
            </span>
          )}
        </span>

        {tag && (
          <TagLabel size="small" color="simple">
            {tag}
          </TagLabel>
        )}
        {isSelected &&
          !isCollapsed &&
          !isSidebar &&
          showCheckmarkOnSelected && (
            <span data-zds>
              <Icon isBlock={true} name="formCheck" size={24} />
            </span>
          )}
        {isExternal && !isCollapsed && (
          <span className={externalIconClassName} data-zds>
            <Icon
              isBlock={true}
              name="arrowOffsite"
              size={isSidebar ? 20 : 18}
              color="UiPrimaryStrongest"
            />
          </span>
        )}
      </span>
      {tooltip}
    </Link>
  );
};

export default UniversalNavLink;
